import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button, 
  CircularProgress, 
  Alert, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  IconButton,
  Collapse,
  Link,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { 
  CloudDownload as CloudDownloadIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassEmptyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  BugReport as BugReportIcon,
  PictureAsPdf as PictureAsPdfIcon
} from '@mui/icons-material';
import { hasPermission, isAdmin } from '../../services/authService';
import OutcomeDistributionChart from '../charts/OutcomeDistributionChart';
import OutcomeByAutomationStatusChart from '../charts/OutcomeByAutomationStatusChart';
import OutcomeByTestSuiteTypeChart from '../charts/OutcomeByTestSuiteTypeChart';
import DaysToReleaseInfo from '../charts/DaysToReleaseInfo';
import BugPieChart from '../charts/BugPieChart';
import AutomatedTestsTable from '../tables/AutomatedTestsTable';
import ManualTestsTable from '../tables/ManualTestsTable';
import ReleaseHealthCharts from '../releases/ReleaseHealthCharts';
import { getAllFixVersions } from '../../services/apiService';
import { fetchAdoTests, getTestExecutions, getReleaseBugs } from '../../services/testService';
import { generateReleaseNotesPdf, generateQaReportDocx, generateReleaseNotesDocx } from '../../services/pdfService';

/**
 * ReleasesTab component for displaying release quality metrics
 * @returns {JSX.Element} ReleasesTab component
 */
const ReleasesTab = () => {
  // State for releases
  const [releases, setReleases] = useState([]);
  const [selectedRelease, setSelectedRelease] = useState('');
  const [selectedReleaseData, setSelectedReleaseData] = useState(null);
  
  // State for tests
  const [testExecutions, setTestExecutions] = useState([]);
  const [loadingTests, setLoadingTests] = useState(false);
  
  // State for errors and loading
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testError, setTestError] = useState(null);
  const [testSuccess, setTestSuccess] = useState(null);
  
  // State for collapsing table content
  const [areaPathExpanded, setAreaPathExpanded] = useState(false);
  const [executionsExpanded, setExecutionsExpanded] = useState(false);
  const [bugsExpanded, setBugsExpanded] = useState(false);
  
  // State for release bugs
  const [releaseBugs, setReleaseBugs] = useState([]);
  const [loadingBugs, setLoadingBugs] = useState(false);
  const [bugError, setBugError] = useState(null);
  
  // State for sorting bugs table
  const [bugSortField, setBugSortField] = useState('bugId');
  const [bugSortDirection, setBugSortDirection] = useState('asc');

  // State for filtering bugs table
  const [showOpenOnly, setShowOpenOnly] = useState(false);
  
  // Fetch releases on component mount
  useEffect(() => {
    fetchReleases();
  }, []);
  
  // Fetch test executions and release bugs when a release is selected
  useEffect(() => {
    if (selectedRelease) {
      fetchTestExecutions(selectedRelease);
      fetchReleaseBugs(selectedRelease);
    }
  }, [selectedRelease]);

  // Sort versions to find the most recent (highest version number)
  const sortVersions = (versions) => {
    return [...versions].sort((a, b) => {
      const regex = /^(\d+)\.(\d+)\.(\d+)$/;
      const matchA = a.version.match(regex);
      const matchB = b.version.match(regex);
      
      if (!matchA || !matchB) return 0;
      
      const [, majorA, minorA, patchA] = matchA.map(Number);
      const [, majorB, minorB, patchB] = matchB.map(Number);
      
      if (majorA !== majorB) return majorB - majorA;
      if (minorA !== minorB) return minorB - minorA;
      return patchB - patchA;
    });
  };

  // Fetch releases from API
  const fetchReleases = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getAllFixVersions();
      
      let releasesData = [];
      
      // Handle different response formats
      if (response && response.data) {
        if (response.data.data && Array.isArray(response.data.data)) {
          releasesData = response.data.data;
        } else if (Array.isArray(response.data)) {
          releasesData = response.data;
        } else {
          releasesData = [];
        }
      } else if (Array.isArray(response)) {
        releasesData = response;
      } else if (response && response.success && Array.isArray(response.data)) {
        releasesData = response.data;
      }
      
      if (releasesData.length > 0) {
        // Filter active releases
        const activeReleases = releasesData.filter(release => release.active);
        const sortedReleases = sortVersions(activeReleases);
        setReleases(sortedReleases);
        
        // Auto-select the most recent release if none is selected
        if (sortedReleases.length > 0 && !selectedRelease) {
          setSelectedRelease(sortedReleases[0].version);
          const releaseData = sortedReleases[0];
          setSelectedReleaseData(releaseData);
        }
      } else {
        setError('Failed to load releases. Invalid response format.');
      }
    } catch (err) {
      console.error('Error fetching releases:', err);
      setError('Failed to load releases. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch test executions for a release
  const fetchTestExecutions = async (releaseVersion) => {
    try {
      setLoadingTests(true);
      setTestError(null);
      setTestSuccess(null);
      
      const response = await getTestExecutions(releaseVersion);
      
      if (response && response.data) {
        setTestExecutions(response.data);
      } else {
        setTestExecutions([]);
      }
    } catch (err) {
      console.error('Error fetching test executions:', err);
      setTestError('Failed to load test executions. Please try again later.');
      setTestExecutions([]);
    } finally {
      setLoadingTests(false);
    }
  };
  
  // Fetch release bugs for a release
  const fetchReleaseBugs = async (releaseVersion) => {
    try {
      setLoadingBugs(true);
      setBugError(null);
      
      const response = await getReleaseBugs(releaseVersion);
      
      if (response && response.data) {
        setReleaseBugs(response.data);
      } else {
        setReleaseBugs([]);
      }
    } catch (err) {
      console.error('Error fetching release bugs:', err);
      setBugError('Failed to load release bugs. Please try again later.');
      setReleaseBugs([]);
    } finally {
      setLoadingBugs(false);
    }
  };
  
  // Handle release selection change
  const handleReleaseChange = (event) => {
    const releaseVersion = event.target.value;
    setSelectedRelease(releaseVersion);
    
    // Find the selected release data
    const releaseData = releases.find(release => release.version === releaseVersion);
    setSelectedReleaseData(releaseData);
  };
  
  // Handle get tests button click
  const handleGetTests = async () => {
    try {
      if (!selectedReleaseData) {
        setTestError('Please select a release first.');
        return;
      }
      
      if (!selectedReleaseData.adoTestPlanFolderId) {
        setTestError('The selected release does not have an ADO Test Plan ID configured.');
        return;
      }
      
      setLoadingTests(true);
      setTestError(null);
      setTestSuccess(null);
      
      const response = await fetchAdoTests(
        selectedReleaseData.adoTestPlanFolderId, 
        selectedReleaseData.version
      );
      
      if (response && response.success) {
        const testCount = response.data ? response.data.length : 0;
        const message = response.message || `Successfully fetched and saved ${testCount} tests.`;
        setTestSuccess(message);
        // Refresh test executions
        await fetchTestExecutions(selectedReleaseData.version);
      } else {
        setTestError(response.message || 'Failed to fetch tests. Please try again later.');
      }
    } catch (err) {
      console.error('Error fetching ADO tests:', err);
      
      // Handle specific error cases
      if (err.message.includes('ADO credentials not configured')) {
        setTestError('Azure DevOps credentials are not configured. Please contact your administrator.');
      } else if (err.response && err.response.status === 401) {
        setTestError('Authentication failed with Azure DevOps. Please check your credentials.');
      } else if (err.response && err.response.status === 404) {
        setTestError(`Test plan ID ${selectedReleaseData.adoTestPlanFolderId} not found in Azure DevOps.`);
      } else {
        setTestError(`Failed to fetch tests: ${err.message}`);
      }
    } finally {
      setLoadingTests(false);
    }
  };
  
  // Handle sorting for bugs table
  const handleBugSort = (field) => {
    if (bugSortField === field) {
      // If already sorting by this field, toggle direction
      setBugSortDirection(bugSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If sorting by a new field, set it and default to ascending
      setBugSortField(field);
      setBugSortDirection('asc');
    }
  };
  
  // Handle DOCX generation
  const handleGenerateDocx = async (type) => {
    try {
      if (!selectedRelease) {
        setError('Please select a release first.');
        return;
      }
      
      // Show loading state
      setLoading(true);
      
      if (type === 'qa-report') {
        // Generate DOCX for QA report
        // The service now handles the download
        await generateQaReportDocx(selectedRelease);
        console.log(`QA report DOCX generation initiated for ${selectedRelease}`);
      }
    } catch (err) {
      console.error(`Error generating ${type} DOCX:`, err);
      setError(`Failed to generate ${type} DOCX. ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle PDF generation
  const handleGeneratePdf = async (type) => {
    try {
      if (!selectedRelease) {
        setError('Please select a release first.');
        return;
      }
      
      // Show loading state
      setLoading(true);
      
      if (type === 'release-notes') {
        // Check if release notes exist
        if (!selectedReleaseData.releaseNotesUrl) {
          setError('No release notes found for this release.');
          setLoading(false);
          return;
        }
        
        // Generate PDF for release notes
        // The PDF service now handles the download
        await generateReleaseNotesPdf(selectedRelease);
        console.log(`Release notes PDF generation initiated for ${selectedRelease}`);
      }
    } catch (err) {
      console.error(`Error generating ${type} PDF:`, err);
      setError(`Failed to generate ${type} PDF. ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle Release Notes DOCX generation
  const handleGenerateReleaseNotesDocx = async () => {
    try {
      if (!selectedRelease) {
        setError('Please select a release first.');
        return;
      }
      
      // Check if release notes exist
      if (!selectedReleaseData.releaseNotesUrl) {
        setError('No release notes found for this release.');
        return;
      }
      
      // Show loading state
      setLoading(true);
      
      // Generate DOCX for release notes
      // The service now handles the download
      await generateReleaseNotesDocx(selectedRelease);
      console.log(`Release notes DOCX generation initiated for ${selectedRelease}`);
    } catch (err) {
      console.error('Error generating release notes DOCX:', err);
      setError(`Failed to generate release notes DOCX. ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Get sorted and filtered bugs
  const getSortedBugs = () => {
    if (!releaseBugs || releaseBugs.length === 0) return [];

    // Filter bugs if "Show Open" is checked
    let filteredBugs = [...releaseBugs];
    if (showOpenOnly) {
      filteredBugs = filteredBugs.filter(bug => bug.state !== 'Closed');
    }

    return filteredBugs.sort((a, b) => {
      let aValue = a[bugSortField];
      let bValue = b[bugSortField];
      
      // Handle special cases for sorting
      if (bugSortField === 'createdDate') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      } else if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      } else if (bugSortField === 'bugId') {
        aValue = parseInt(aValue) || 0;
        bValue = parseInt(bValue) || 0;
      }
      
      // Sort based on direction
      if (bugSortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };
  
  // Render sort indicator
  const renderSortIndicator = (field) => {
    if (bugSortField !== field) return null;
    
    return (
      <span style={{ marginLeft: '4px' }}>
        {bugSortDirection === 'asc' ? '▲' : '▼'}
      </span>
    );
  };
  
  // Render outcome chip based on test outcome
  const renderOutcomeChip = (outcome) => {
    switch (outcome) {
      case 'Passed':
        return <Chip 
          icon={<CheckCircleIcon />} 
          label="Passed" 
          color="success" 
          size="small" 
        />;
      case 'Failed':
        return <Chip 
          icon={<CancelIcon />} 
          label="Failed" 
          color="error" 
          size="small" 
        />;
      case 'Not Run':
      default:
        return <Chip 
          icon={<HourglassEmptyIcon />} 
          label="Not Run" 
          color="default" 
          size="small" 
        />;
    }
  };
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Releases
      </Typography>
      <Typography variant="body1" paragraph>
        Manage and track release quality metrics and status.
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <FormControl sx={{ minWidth: 200, mr: 2 }}>
                <InputLabel id="release-select-label">Release Version</InputLabel>
                <Select
                  labelId="release-select-label"
                  id="release-select"
                  value={selectedRelease}
                  label="Release Version"
                  onChange={handleReleaseChange}
                  disabled={loading || releases.length === 0}
                >
                  {releases.map((release) => (
                    <MenuItem key={release._id} value={release.version}>
                      {release.version}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CloudDownloadIcon />}
                  onClick={handleGetTests}
                  disabled={
                    !selectedRelease || 
                    loadingTests || 
                    (selectedReleaseData && 
                     selectedReleaseData.releaseDate && 
                     new Date(selectedReleaseData.releaseDate) < new Date() && 
                     !isAdmin()) || 
                    !hasPermission('ado:fetch')
                  }
                  title={
                    selectedReleaseData && 
                    selectedReleaseData.releaseDate && 
                    new Date(selectedReleaseData.releaseDate) < new Date() && 
                    !isAdmin() 
                      ? "Only Admin users can fetch data for past releases" 
                      : "Get latest test data from Azure DevOps"
                  }
                >
                  {loadingTests ? <CircularProgress size={24} /> : 'Get Latest ADO Tests'}
                </Button>
                
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<CloudDownloadIcon />}
                  onClick={() => handleGenerateReleaseNotesDocx()}
                  disabled={!selectedRelease || !selectedReleaseData?.releaseNotesUrl}
                  title="Generate release notes document"
                >
                  Release Notes
                </Button>
                
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<CloudDownloadIcon />}
                  onClick={() => handleGenerateDocx('qa-report')}
                  disabled={!selectedRelease}
                  title="Generate validation report document"
                >
                  Validation Report
                </Button>
              </Box>
            </Box>
            
            {testError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {testError}
              </Alert>
            )}
            
            {testSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {testSuccess}
              </Alert>
            )}
            
            {selectedReleaseData && (
              <Box mb={2}>
                <Typography variant="subtitle1">
                  Release Details
                </Typography>
                <Typography variant="body2">
                  <strong>Version:</strong> {selectedReleaseData.version}
                </Typography>
                <Typography variant="body2">
                  <strong>Description:</strong> {selectedReleaseData.description || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Release Date:</strong> {selectedReleaseData.releaseDate 
                    ? new Date(selectedReleaseData.releaseDate).toLocaleDateString() 
                    : 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>ADO Test Plan ID:</strong> {selectedReleaseData.adoTestPlanFolderId || 'N/A'}
                </Typography>
              </Box>
            )}
            
            {/* Execution Charts Section */}
            <Box mb={4}>
              <Typography variant="subtitle1" gutterBottom>
                Test Execution Charts
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  {testExecutions.length > 0 ? (
                    <Paper elevation={2} sx={{ p: 2 }}>
                      <OutcomeDistributionChart testExecutions={testExecutions} />
                    </Paper>
                  ) : (
                    <Paper elevation={2} sx={{ p: 2, height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No test data available. Use the "Get Latest ADO Tests" button to fetch tests.
                      </Typography>
                    </Paper>
                  )}
                </Grid>
                <Grid item xs={12} md={4}>
                  {testExecutions.length > 0 ? (
                    <Paper elevation={2} sx={{ p: 2 }}>
                      <OutcomeByTestSuiteTypeChart testExecutions={testExecutions} />
                    </Paper>
                  ) : (
                    <Paper elevation={2} sx={{ p: 2, height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No test data available. Use the "Get Latest ADO Tests" button to fetch tests.
                      </Typography>
                    </Paper>
                  )}
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper elevation={2} sx={{ p: 2, height: '300px' }}>
                    <DaysToReleaseInfo releaseData={selectedReleaseData} />
                  </Paper>
                </Grid>
              </Grid>
            </Box>
            
            {/* Bug Charts Section */}
            {selectedRelease && releaseBugs.length > 0 && (
              <Box mb={4}>
                <Typography variant="subtitle1" gutterBottom>
                  Bug Distribution for {selectedRelease}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Paper elevation={2}>
                      <BugPieChart 
                        data={releaseBugs} 
                        title="Bugs by Priority" 
                        groupBy="priority" 
                      />
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper elevation={2}>
                      <BugPieChart 
                        data={releaseBugs} 
                        title="Bugs by Status" 
                        groupBy="state" 
                      />
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper elevation={2}>
                      <BugPieChart 
                        data={releaseBugs} 
                        title="Bugs by Issue Source" 
                        groupBy="issueSource" 
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
            
            {/* Execution Status by Area Path Section */}
            {testExecutions.length > 0 && (
              <Box mb={4}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1">
                    Execution Status by Area Path
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                      {areaPathExpanded ? "Hide" : "Show"} area path breakdown
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => setAreaPathExpanded(!areaPathExpanded)}
                      aria-label={areaPathExpanded ? "Collapse table" : "Expand table"}
                      color="primary"
                    >
                      {areaPathExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                </Box>
                
                <Collapse in={areaPathExpanded}>
                    <Box mb={4}>
                      <Typography variant="subtitle1" gutterBottom>
                        Automated Tests ({testExecutions.filter(test => 
                          test.automationStatus === 'Automated-UI' || 
                          test.automationStatus === 'Automated-API').length} tests)
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Area Path</TableCell>
                              <TableCell align="center">Passed</TableCell>
                              <TableCell align="center">Failed</TableCell>
                              <TableCell align="center">Not Run</TableCell>
                              <TableCell align="center">Total</TableCell>
                              <TableCell align="center">Pass Rate</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {/* We'll use the filtered data directly here */}
                            {Object.values(testExecutions
                              .filter(test => 
                                test.automationStatus === 'Automated-UI' || 
                                test.automationStatus === 'Automated-API')
                              .reduce((acc, test) => {
                                const areaPath = test.areaPath || 'Not Specified';
                                const outcome = test.outcome || 'Not Run';
                                
                                if (!acc[areaPath]) {
                                  acc[areaPath] = {
                                    areaPath,
                                    Passed: 0,
                                    Failed: 0,
                                    'Not Run': 0,
                                    total: 0
                                  };
                                }
                                
                                acc[areaPath][outcome] = (acc[areaPath][outcome] || 0) + 1;
                                acc[areaPath].total += 1;
                                
                                return acc;
                              }, {}))
                              .sort((a, b) => a.areaPath.localeCompare(b.areaPath))
                              .map(row => (
                                <TableRow key={row.areaPath}>
                                  <TableCell>{row.areaPath}</TableCell>
                                  <TableCell align="center">
                                    {row.Passed > 0 ? (
                                      <Chip
                                        label={row.Passed}
                                        size="small"
                                        sx={{ 
                                          bgcolor: '#4caf50', 
                                          color: 'white',
                                          fontSize: '0.75rem',
                                          '& .MuiChip-label': { px: 1 }
                                        }}
                                      />
                                    ) : 0}
                                  </TableCell>
                                  <TableCell align="center">
                                    {row.Failed > 0 ? (
                                      <Chip
                                        label={row.Failed}
                                        size="small"
                                        sx={{ 
                                          bgcolor: '#f44336', 
                                          color: 'white',
                                          fontSize: '0.75rem',
                                          '& .MuiChip-label': { px: 1 }
                                        }}
                                      />
                                    ) : 0}
                                  </TableCell>
                                  <TableCell align="center">
                                    {row['Not Run'] > 0 ? (
                                      <Chip
                                        label={row['Not Run']}
                                        size="small"
                                        sx={{ 
                                          bgcolor: '#9e9e9e', 
                                          color: 'white',
                                          fontSize: '0.75rem',
                                          '& .MuiChip-label': { px: 1 }
                                        }}
                                      />
                                    ) : 0}
                                  </TableCell>
                                  <TableCell align="center">{row.total}</TableCell>
                                  <TableCell align="center">
                                    {row.Passed + row.Failed > 0 
                                      ? `${((row.Passed / (row.Passed + row.Failed)) * 100).toFixed(1)}%` 
                                      : 'N/A'}
                                  </TableCell>
                                </TableRow>
                              ))
                            }
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle1" gutterBottom>
                        Manual Tests ({testExecutions.filter(test => 
                          test.automationStatus !== 'Automated-UI' && 
                          test.automationStatus !== 'Automated-API').length} tests)
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Area Path</TableCell>
                              <TableCell align="center">Passed</TableCell>
                              <TableCell align="center">Failed</TableCell>
                              <TableCell align="center">Not Run</TableCell>
                              <TableCell align="center">Total</TableCell>
                              <TableCell align="center">Pass Rate</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {/* We'll use the filtered data directly here */}
                            {Object.values(testExecutions
                              .filter(test => 
                                test.automationStatus !== 'Automated-UI' && 
                                test.automationStatus !== 'Automated-API')
                              .reduce((acc, test) => {
                                const areaPath = test.areaPath || 'Not Specified';
                                const outcome = test.outcome || 'Not Run';
                                
                                if (!acc[areaPath]) {
                                  acc[areaPath] = {
                                    areaPath,
                                    Passed: 0,
                                    Failed: 0,
                                    'Not Run': 0,
                                    total: 0
                                  };
                                }
                                
                                acc[areaPath][outcome] = (acc[areaPath][outcome] || 0) + 1;
                                acc[areaPath].total += 1;
                                
                                return acc;
                              }, {}))
                              .sort((a, b) => a.areaPath.localeCompare(b.areaPath))
                              .map(row => (
                                <TableRow key={row.areaPath}>
                                  <TableCell>{row.areaPath}</TableCell>
                                  <TableCell align="center">
                                    {row.Passed > 0 ? (
                                      <Chip
                                        label={row.Passed}
                                        size="small"
                                        sx={{ 
                                          bgcolor: '#4caf50', 
                                          color: 'white',
                                          fontSize: '0.75rem',
                                          '& .MuiChip-label': { px: 1 }
                                        }}
                                      />
                                    ) : 0}
                                  </TableCell>
                                  <TableCell align="center">
                                    {row.Failed > 0 ? (
                                      <Chip
                                        label={row.Failed}
                                        size="small"
                                        sx={{ 
                                          bgcolor: '#f44336', 
                                          color: 'white',
                                          fontSize: '0.75rem',
                                          '& .MuiChip-label': { px: 1 }
                                        }}
                                      />
                                    ) : 0}
                                  </TableCell>
                                  <TableCell align="center">
                                    {row['Not Run'] > 0 ? (
                                      <Chip
                                        label={row['Not Run']}
                                        size="small"
                                        sx={{ 
                                          bgcolor: '#9e9e9e', 
                                          color: 'white',
                                          fontSize: '0.75rem',
                                          '& .MuiChip-label': { px: 1 }
                                        }}
                                      />
                                    ) : 0}
                                  </TableCell>
                                  <TableCell align="center">{row.total}</TableCell>
                                  <TableCell align="center">
                                    {row.Passed + row.Failed > 0 
                                      ? `${((row.Passed / (row.Passed + row.Failed)) * 100).toFixed(1)}%` 
                                      : 'N/A'}
                                  </TableCell>
                                </TableRow>
                              ))
                            }
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Collapse>
              </Box>
            )}
            
            {/* Table Section with Collapse */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1">
                Test Executions Table
              </Typography>
              
              {testExecutions.length > 0 && (
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                    {executionsExpanded ? "Hide" : "Show"} {testExecutions.length} test executions
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => setExecutionsExpanded(!executionsExpanded)}
                    aria-label={executionsExpanded ? "Collapse table" : "Expand table"}
                    color="primary"
                  >
                    {executionsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
              )}
            </Box>
            
            {loadingTests ? (
              <Box display="flex" justifyContent="center" my={2}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  Fetching tests from Azure DevOps...
                </Typography>
              </Box>
            ) : testExecutions.length > 0 ? (
              <Collapse in={executionsExpanded}>
                <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Test ID</TableCell>
                      <TableCell>Test Name</TableCell>
                      <TableCell>Area Path</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Team</TableCell>
                      <TableCell>Automation Status</TableCell>
                      <TableCell>Component</TableCell>
                      <TableCell>Test Suite Type</TableCell>
                      <TableCell>Outcome</TableCell>
                      <TableCell>Executed Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {testExecutions.map((test) => (
                      <TableRow key={test.testId}>
                        <TableCell>
                        <a 
                          href={`https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${test.testId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'underline', color: '#0078d4' }}
                        >
                          {test.testId}
                        </a>
                      </TableCell>
                        <TableCell>{test.testName}</TableCell>
                        <TableCell>{test.areaPath || 'N/A'}</TableCell>
                        <TableCell>{test.priority || 'N/A'}</TableCell>
                        <TableCell>{test.team || 'N/A'}</TableCell>
                        <TableCell>{test.automationStatus || 'N/A'}</TableCell>
                        <TableCell>{test.component || 'N/A'}</TableCell>
                        <TableCell>{test.testSuiteType || 'N/A'}</TableCell>
                        <TableCell>{renderOutcomeChip(test.outcome)}</TableCell>
                        <TableCell>
                          {test.executedDate 
                            ? new Date(test.executedDate).toLocaleString() 
                            : 'Not Executed'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </TableContainer>
              </Collapse>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No test executions found for this release. Use the "Get Latest ADO Tests" button to fetch tests (only the latest execution for each test will be included).
              </Typography>
            )}
  
            {/* Bugs Raised in Release Section */}
            {selectedRelease && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1">
                    Bugs Raised in {selectedRelease}
                  </Typography>
                  
                  {releaseBugs.length > 0 && (
                    <Box display="flex" alignItems="center">
                      <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                        {bugsExpanded ? "Hide" : "Show"} {releaseBugs.length} bugs
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => setBugsExpanded(!bugsExpanded)}
                        aria-label={bugsExpanded ? "Collapse table" : "Expand table"}
                        color="primary"
                      >
                        {bugsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  )}
                </Box>
                
                {bugError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {bugError}
                  </Alert>
                )}

                {/* Show Open Bugs Filter Checkbox */}
                {releaseBugs.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showOpenOnly}
                          onChange={(e) => setShowOpenOnly(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Show Open"
                    />
                  </Box>
                )}
                
                {loadingBugs ? (
                  <Box display="flex" justifyContent="center" my={2}>
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                      Fetching bugs for release {selectedRelease}...
                    </Typography>
                  </Box>
                ) : releaseBugs.length > 0 ? (
                  <Collapse in={bugsExpanded}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell 
                              onClick={() => handleBugSort('bugId')}
                              style={{ cursor: 'pointer' }}
                            >
                              Bug ID {renderSortIndicator('bugId')}
                            </TableCell>
                            <TableCell 
                              onClick={() => handleBugSort('title')}
                              style={{ cursor: 'pointer' }}
                            >
                              Title {renderSortIndicator('title')}
                            </TableCell>
                            <TableCell 
                              onClick={() => handleBugSort('state')}
                              style={{ cursor: 'pointer' }}
                            >
                              State {renderSortIndicator('state')}
                            </TableCell>
                            <TableCell 
                              onClick={() => handleBugSort('priority')}
                              style={{ cursor: 'pointer' }}
                            >
                              Priority {renderSortIndicator('priority')}
                            </TableCell>
                            <TableCell 
                              onClick={() => handleBugSort('team')}
                              style={{ cursor: 'pointer' }}
                            >
                              Team {renderSortIndicator('team')}
                            </TableCell>
                            <TableCell 
                              onClick={() => handleBugSort('fixVersionDummy')}
                              style={{ cursor: 'pointer' }}
                            >
                              Fix Version {renderSortIndicator('fixVersionDummy')}
                            </TableCell>
                            <TableCell 
                              onClick={() => handleBugSort('issueSource')}
                              style={{ cursor: 'pointer' }}
                            >
                              Issue Source {renderSortIndicator('issueSource')}
                            </TableCell>
                            <TableCell 
                              onClick={() => handleBugSort('createdDate')}
                              style={{ cursor: 'pointer' }}
                            >
                              Created Date {renderSortIndicator('createdDate')}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getSortedBugs().map((bug) => (
                            <TableRow key={bug.bugId}>
                              <TableCell>
                                <a 
                                  href={`https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${bug.bugId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ textDecoration: 'underline', color: '#0078d4' }}
                                >
                                  {bug.bugId}
                                </a>
                              </TableCell>
                              <TableCell>{bug.title}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={bug.state} 
                                  size="small"
                                  color={bug.state === 'Closed' ? 'success' : 'default'}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={bug.priority} 
                                  size="small"
                                  color={
                                    bug.priority === 'P1' ? 'error' :
                                    bug.priority === 'P2' ? 'warning' :
                                    bug.priority === 'P3' ? 'info' : 'default'
                                  }
                                />
                              </TableCell>
                              <TableCell>{bug.team || 'N/A'}</TableCell>
                              <TableCell>{bug.fixVersionDummy || 'N/A'}</TableCell>
                              <TableCell>{bug.issueSource || 'N/A'}</TableCell>
                              <TableCell>
                                {bug.createdDate 
                                  ? new Date(bug.createdDate).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) 
                                  : 'N/A'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Collapse>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No bugs found for this release. Bugs are fetched when you click the "Get Latest ADO Tests" button.
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
        
      </Grid>
    </Box>
  );
};

export default ReleasesTab;
